import React from "react"
import * as AuthApi from "features/auth/api/AuthApi"
import * as UserApi from "features/account/api/UserApi"
import { http } from "services/http"
import browserStorageService, {
	localStorageService,
	SESSION_CONFIG_KEY,
} from "services/browserStorage"
import { ErrorFn } from "models"
import { useUser, useAlert } from "hooks"

type Login = (
	mail: string,
	password: string,
	operator: string
) => Promise<Record<string, never> | ErrorFn>

type AuthContextInterface = {
	isLoggedIn: boolean
	login: Login
	logout: () => void
}

const AuthContext = React.createContext<AuthContextInterface | undefined>(undefined)

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const { user, dispatch: dispatchUser } = useUser()
	const { alert } = useAlert()

	const isLoggedIn = React.useMemo(() => !!user, [user])

	const fetchUser = React.useCallback(async () => {
		const { res: fetchedUser, getError } = await UserApi.getUser()
		if (getError) throw getError()

		if (fetchedUser) {
			dispatchUser({ type: "USER", user: fetchedUser })
		} else {
			throw getError()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const login: Login = async (mail, password, operator) => {
		if (user) return {}

		if (mail && password && operator) {
			const { res: newEntity, getError } = await AuthApi.signIn(mail, password, operator)

			if (getError()) throw getError()

			if (newEntity.account_id) {
				// NB: we save the entity here "just in case" for further use
				// but only the user is required for the login
				dispatchUser({ type: "LOGIN", entity: newEntity })
				await fetchUser()
				return {}
			}
		} else {
			const errorFn = { getError: () => {} }
			return errorFn as ErrorFn
		}
	}

	const logout = () => {
		http.clearConfig()
		localStorageService.clearSession()
		dispatchUser({ type: "LOGOUT" })
	}

	React.useEffect(() => {
		// Init http service
		http.initHttp({ logout, alert })
		try {
			// Auto-login if a token is already saved
			const tokenIsAvailable = browserStorageService.get(SESSION_CONFIG_KEY)
			if (tokenIsAvailable) fetchUser()
			if (!tokenIsAvailable) logout()
		} catch (error) {
			console.log(error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<AuthContext.Provider value={{ isLoggedIn, login, logout }}>
			{children}
		</AuthContext.Provider>
	)
}

const useAuth = () => {
	const context = React.useContext(AuthContext)
	if (context === undefined) {
		throw new Error("useAuth must be used within a AuthProvider")
	}
	return context
}

export { useAuth, AuthProvider }
